"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { TocItem } from "@/lib/toc";

// In-page header navigation with scroll-spy. Highlights the section currently
// near the top of the viewport and smooth-scrolls on click.
export function BlogToc({
  headings,
  className,
  scrollerClassName,
}: {
  headings: TocItem[];
  className?: string;
  scrollerClassName?: string;
}) {
  const navRef = useRef<HTMLElement>(null);
  const linkRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const [active, setActive] = useState<string>(headings[0]?.id ?? "");

  useEffect(() => {
    const els = headings
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;

    let frame = 0;

    const updateActive = () => {
      frame = 0;
      const activationLine = Math.min(window.innerHeight * 0.3, 180);
      let current = els[0].id;

      for (const el of els) {
        if (el.getBoundingClientRect().top <= activationLine) {
          current = el.id;
        } else {
          break;
        }
      }

      setActive((previous) => (previous === current ? previous : current));
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateActive);
    };

    updateActive();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, [headings]);

  useEffect(() => {
    const link = linkRefs.current[active];
    const scroller = navRef.current?.querySelector<HTMLElement>(
      "[data-toc-scroller]"
    );
    if (!link || !scroller) return;

    const target =
      link.offsetTop - scroller.clientHeight / 2 + link.offsetHeight / 2;

    scroller.scrollTo({
      top: Math.max(target, 0),
      behavior: "smooth",
    });
  }, [active]);

  function onClick(e: React.MouseEvent, id: string) {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;

    const start = window.scrollY;
    // Offset by the heading's own scroll-margin-top (set to clear the sticky
    // header in blog-article's prose styles) so a TOC click lands in the same
    // spot as a native #hash jump — below the top nav, not behind it.
    const scrollMarginTop =
      parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
    const target =
      el.getBoundingClientRect().top + window.scrollY - scrollMarginTop;
    const duration = 180;
    // Seed the clock from the first rAF timestamp instead of performance.now()
    // (calling it here would be flagged as an impure read).
    let startedAt: number | null = null;

    const animate = (now: number) => {
      if (startedAt === null) startedAt = now;
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      window.scrollTo(0, start + (target - start) * eased);

      if (progress < 1) {
        window.requestAnimationFrame(animate);
      }
    };

    window.requestAnimationFrame(animate);
    history.replaceState(null, "", `#${id}`);
    setActive(id);
  }

  return (
    <nav
      ref={navRef}
      className={cn("text-sm", className)}
      aria-label="On this page"
    >
      <p className="mb-3 font-medium text-foreground">On this page</p>
      <div
        data-toc-scroller
        className={cn(
          "max-h-[calc(100dvh-8rem)] overflow-y-auto pr-2 [scrollbar-width:thin]",
          scrollerClassName
        )}
      >
        <ul className="space-y-2 border-l border-border">
          {headings.map((h) => (
            <li key={h.id} style={{ paddingLeft: (h.level - 2) * 12 }}>
              <a
                ref={(node) => {
                  linkRefs.current[h.id] = node;
                }}
                href={`#${h.id}`}
                onClick={(e) => onClick(e, h.id)}
                className={cn(
                  "-ml-px block border-l-2 pl-3 leading-snug transition-colors",
                  active === h.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
